import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { RoleBadge } from '@/components/ui'
import { useActiveWorkspace, useAuthStore } from '@/stores/auth'

/**
 * Переключатель активного рабочего пространства.
 *
 * Сценарий, ради которого он нужен, — бухгалтерская компания с несколькими
 * клиентами: переключение должно быть в один клик и на каждом экране.
 *
 * При смене пространства кэш запросов сбрасывается полностью: данные разных
 * арендаторов не должны на мгновение подмешиваться друг к другу, даже
 * визуально.
 */
export function WorkspaceSwitcher() {
  const workspaces = useAuthStore((state) => state.workspaces)
  const setActiveWorkspace = useAuthStore((state) => state.setActiveWorkspace)
  const active = useActiveWorkspace()
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return

    function handleClickOutside(event: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    function handleEscape(event: KeyboardEvent): void {
      if (event.key === 'Escape') setIsOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  function handleSelect(workspaceId: string): void {
    setIsOpen(false)
    if (workspaceId === active?.workspace_id) return

    setActiveWorkspace(workspaceId)
    void queryClient.clear()
  }

  if (!active) {
    return <p className="px-1 text-sm text-slate-500">Нет доступных пространств</p>
  }

  const hasChoice = workspaces.length > 1

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => hasChoice && setIsOpen((value) => !value)}
        className={`flex w-full items-center justify-between gap-2 rounded-lg px-2 py-2 text-left ${
          hasChoice ? 'hover:bg-slate-50' : 'cursor-default'
        }`}
        aria-haspopup={hasChoice ? 'listbox' : undefined}
        aria-expanded={hasChoice ? isOpen : undefined}
      >
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-slate-900">{active.name}</span>
          <span className="block truncate text-xs text-slate-500">{active.slug}</span>
        </span>
        {hasChoice && (
          <svg
            className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </button>

      {isOpen && (
        <ul
          className="absolute left-0 right-0 z-20 mt-1 max-h-72 overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
          role="listbox"
        >
          {workspaces.map((item) => (
            <li key={item.workspace_id}>
              <button
                type="button"
                role="option"
                aria-selected={item.workspace_id === active.workspace_id}
                onClick={() => handleSelect(item.workspace_id)}
                className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                  item.workspace_id === active.workspace_id ? 'bg-brand-50' : ''
                }`}
              >
                <span className="min-w-0 truncate">{item.name}</span>
                <RoleBadge role={item.role} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
